import * as yup from 'yup'
import { WF_STEP_PROCESSING_TYPE, WF_STEP_TYPE, RA_ACTION_TYPE } from '../../../../constants/workflow-steps'

// Set custom error messages
yup.setLocale({
  mixed: {
    required: 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
  }
})

export function isValidFilename(value: string | null | undefined) {
  // filename is . or .. (aka current/parent)
  if (value) {
    if (value === '.' || value === '..') {
      return false
    } else if (
      value.indexOf('/') > -1 ||
      value.indexOf('\\') > -1 ||
      value.indexOf(':') > -1 ||
      value.indexOf('*') > -1 ||
      value.indexOf('?') > -1 ||
      value.indexOf('"') > -1 ||
      value.indexOf('<') > -1 ||
      value.indexOf('>') > -1 ||
      value.indexOf('|') > -1 ||
      value.indexOf('\t') > -1 ||
      value.indexOf('\r') > -1 ||
      value.indexOf('\n') > -1 ||
      value.indexOf('\f') > -1 ||
      value.indexOf('\b') > -1
    ) {
      return false
    }
    // first or last character is a space
    else if (/^\s+|\s+$/.test(value)) {
      return false
    }
  }
  return true
}

const taskTeamStepLabel = yup
  .object()
  .shape({
    id: yup.number().nullable(),
    label: yup.string().nullable(),
    value: yup.number().nullable()
  })
  .nullable()
  .transform((value: any, originalValue: any) => (originalValue === '' ? null : value))
  .default(null)

const someAtOnceErrorMessage = 'The # of users cannot be empty or set to 0, it must be set to 1 or more'

const nofAllValue = yup
  .mixed()
  .nullable()
  .transform((value: any) => (value === '' || isNaN(value) ? null : value))
  .when('processingType', {
    is: WF_STEP_PROCESSING_TYPE.N_OF_ALL,
    // Use yup.number() to explicitly start a number schema for this branch
    then: () =>
      yup
        .number()
        .typeError(someAtOnceErrorMessage) // Handles cases where transform fails
        .min(1, someAtOnceErrorMessage)
        .required(someAtOnceErrorMessage),
    otherwise: (schema: any) => schema.nullable()
  })

export const ValidationSchema = () => {
  return yup.object().shape({
    fileName: yup
      .string()
      .trim()
      .max(255)
      .test('isValidFilename', 'FILENAME_CONTAINS_INVALID_CHARACTERS', isValidFilename)
      .when('selectedDocTypeObj', {
        is: (selectedDocTypeObj: any) => selectedDocTypeObj?.autonamingEnabled,
        then: (schema: any) => schema.required(),
        otherwise: (schema: any) => schema.nullable()
      })
      .nullable(),

    expiresAtDate: yup
      .object()
      .nullable()
      .when('selectedDocTypeObj.requireExpirationDate', {
        is: true,
        then: (schema: any) => schema.required()
      }),

    justificationTextId: yup
      .number()
      .min(0)
      .nullable()
      .when('selectedDocTypeObj.requireJustification', {
        is: true,
        then: (schema: any) => schema.required()
      }),

    description: yup.string().max(254).required(),

    crdoctype: yup.object().nullable().required(),

    typeData: yup.object().shape({
      initialApprovers: yup.object().shape({
        type: yup
          .string()
          .oneOf(Object.values(WF_STEP_TYPE))
          .default(() => WF_STEP_TYPE.NOT_DEFINED),
        steps: yup
          .array()
          .of(
            yup.object().shape({
              taskTeamStepLabel: taskTeamStepLabel,
              processingType: yup.string().nullable().required(),
              nofAllValue: nofAllValue,
              merged: yup.array()
            })
          )
          .nullable()
          .default(() => [])
      }),
      collaborators: yup.object().shape({
        type: yup
          .string()
          .oneOf(Object.values(WF_STEP_TYPE))
          .default(() => WF_STEP_TYPE.USE_INITIATOR),
        steps: yup
          .array()
          .of(
            yup.object().shape({
              taskTeamStepLabel: taskTeamStepLabel,
              processingType: yup.string().nullable().required(),
              nofAllValue: nofAllValue,
              merged: yup.array().min(1, 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR').required()
            })
          )
          .when('type', {
            is: (val: string) => val === WF_STEP_TYPE.USE_PREDEFINED || val === WF_STEP_TYPE.SETUP_WITH_WORKFLOW,
            // FIX: Using .min(1) to block empty arrays []
            then: (schema: any) => schema.min(1, 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR').required(),
            otherwise: (schema: any) => schema.notRequired()
          })
          .nullable()
      }),

      // Apply the same 'steps' fix to reviewers and approvers if they use SETUP_WITH_WORKFLOW
      reviewers: yup.object().shape({
        type: yup
          .string()
          .oneOf(Object.values(WF_STEP_TYPE))
          .default(() => WF_STEP_TYPE.NOT_DEFINED),
        steps: yup
          .array()
          .of(
            yup.object().shape({
              taskTeamStepLabel: taskTeamStepLabel,
              processingType: yup.string().nullable().required(),
              nofAllValue: nofAllValue,
              merged: yup.array()
            })
          )
          .nullable()
      }),

      approvers: yup.object().shape({
        type: yup
          .string()
          .oneOf(Object.values(WF_STEP_TYPE))
          .default(() => WF_STEP_TYPE.NOT_DEFINED),
        steps: yup
          .array()
          .of(
            yup.object().shape({
              taskTeamStepLabel: taskTeamStepLabel,
              processingType: yup.string().nullable().required(),
              nofAllValue: nofAllValue,
              merged: yup.array()
            })
          )
          .nullable()
      }),

      trainees: yup.object().shape({
        createRAActionType: yup
          .string()
          .oneOf(Object.values(RA_ACTION_TYPE))
          .default(() => RA_ACTION_TYPE.FOR_NOBODY),
        usersGroups: yup
          .array()
          .default(() => [])
          .nullable()
      })
    }),

    rejectionReviewers: yup.object().shape({
      type: yup
        .string()
        .oneOf(Object.values(WF_STEP_TYPE))
        .default(() => WF_STEP_TYPE.USE_INITIATOR),
      usersGroups: yup
        .array()
        .when('type', {
          is: WF_STEP_TYPE.USE_PREDEFINED,
          then: yup.array().required()
        })
        .default(() => [])
        .nullable()
    })
  })
}

export default ValidationSchema
